import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Zap, Bot, Plug, ArrowRight } from "lucide-react";
import { useI18n } from "@/i18n";

export function SkillsSection() {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const cards = [
    { icon: Zap, title: t.home.skillsCard1Title, desc: t.home.skillsCard1Desc },
    { icon: Bot, title: t.home.skillsCard2Title, desc: t.home.skillsCard2Desc },
    { icon: Plug, title: t.home.skillsCard3Title, desc: t.home.skillsCard3Desc },
  ];


  return (
    <section ref={sectionRef} className="w-full py-20 md:py-28 bg-white">
      <div className="mx-auto max-w-[1152px] px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#0a1733]">
            {t.home.skillsSectionTitle}
          </h2>
          <p className="mt-4 text-lg text-[#7a818e]">
            {t.home.skillsSectionSubtitle}
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {cards.map((card, i) => {
            const Icon = card.icon;
            return (
              <Card
                key={i}
                className={`bg-white border border-[#e5e5e5] rounded-[10px] shadow-none hover:border-[#d0d0d0] transition-all duration-500 ${
                  isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-center w-12 h-12 rounded-[10px] bg-[#f5f5f5] mb-4">
                    <Icon className="size-6 text-[#0a1733]" />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0a1733] mb-2">{card.title}</h3>
                  <p className="text-sm text-[#7a818e] leading-relaxed">{card.desc}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Terminal code block */}
        <div
          className={`mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
          style={{ transitionDelay: "300ms" }}
        >
          <div className="bg-white border border-[#e5e5e5] rounded-[10px] overflow-hidden max-w-2xl mx-auto">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#e5e5e5]">
              <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
              <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
              <div className="w-3 h-3 rounded-full bg-[#e5e5e5]" />
              <span className="ml-2 text-xs text-[#7a818e] font-mono">terminal</span>
            </div>
            <div className="p-5 font-mono text-sm">
              <span className="text-[#ff4a01]">$</span>{" "}
              <span className="text-[#1a1a1a]">npx skills add hau823823/gen-paylink-govilo</span>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button asChild size="lg" className="bg-[#ff4a01] text-white hover:bg-[#e54400] border-0 rounded-[10px]">
            <Link href="/skills">
              {t.home.skillsCta}
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
